/**
 * TransactionFilters (FE-2, §4.3). The full filter set for the transactions list
 * and CSV export: free-text search, sort, and a "Filter" popover holding
 * account / bucket / review-status / status / date / amount / tag / deleted
 * controls. Every change writes through `patch`, which round-trips to the URL.
 */
import { useState, type ReactNode } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { Input, type InputProps } from '../../components/ui/Input.tsx';
import { Popover } from '../../components/ui/Popover.tsx';
import { Select } from '../../components/ui/Select.tsx';
import { TagInput } from '../../components/ui/TagInput.tsx';
import { formatAmountInput, parseAmountInput } from '../../lib/format.ts';
import { useAccounts } from '../../lib/hooks/useAccounts.ts';
import { useBuckets } from '../../lib/hooks/useBuckets.ts';
import { countActiveFilters } from '../../lib/hooks/useTransactions.ts';
import { REVIEW_STATUS, STATUS } from '../../lib/labels.ts';
import type {
  ReviewStatus,
  TransactionFilters as Filters,
  TransactionSort,
  TransactionStatus,
} from '../../lib/types.ts';

export interface TransactionFiltersProps {
  filters: Filters;
  patch: (partial: Partial<Filters>) => void;
  reset: () => void;
}

interface SortChoice {
  value: TransactionSort;
  label: string;
}

const SORT_OPTIONS: SortChoice[] = [
  { value: '-booking_date', label: 'Datum (neueste zuerst)' },
  { value: 'booking_date', label: 'Datum (älteste zuerst)' },
  { value: '-amount', label: 'Betrag (absteigend)' },
  { value: 'amount', label: 'Betrag (aufsteigend)' },
  { value: '-created_at', label: 'Erfasst (neueste zuerst)' },
  { value: 'created_at', label: 'Erfasst (älteste zuerst)' },
];

const DEFAULT_SORT: TransactionSort = '-booking_date';
const REVIEW_ORDER: ReviewStatus[] = ['pending', 'reviewed', 'skipped'];
const STATUS_ORDER: TransactionStatus[] = ['booked', 'pending'];

/** Toggle a value in an optional array; returns undefined when it becomes empty. */
function toggle<T>(list: readonly T[] | undefined, value: T): T[] | undefined {
  const set = new Set(list ?? []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  const next = [...set];
  return next.length ? next : undefined;
}

/**
 * A text input that keeps its draft locally and only commits (trimmed) on blur
 * or Enter — remounted by the caller via `key` when the external value changes,
 * so no state-syncing effect is needed.
 */
type CommitInputProps = Omit<InputProps, 'value' | 'onChange' | 'onBlur' | 'onKeyDown'> & {
  initialValue: string;
  onCommit: (value: string) => void;
};

function CommitInput({ initialValue, onCommit, ...inputProps }: CommitInputProps) {
  const [draft, setDraft] = useState(initialValue);
  return (
    <Input
      {...inputProps}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => onCommit(draft.trim())}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onCommit(draft.trim());
        }
      }}
    />
  );
}

function FilterSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
        {title}
      </p>
      {children}
    </div>
  );
}

function CheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm text-slate-600 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100">
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500 dark:focus-visible:outline-sky-500"
      />
      <span className="truncate">{label}</span>
    </label>
  );
}

export function TransactionFilters({ filters, patch, reset }: TransactionFiltersProps) {
  const accounts = useAccounts();
  const buckets = useBuckets();
  const [open, setOpen] = useState(false);
  const [amountInvalid, setAmountInvalid] = useState({ min: false, max: false });

  const activeCount = countActiveFilters(filters);

  /**
   * Commit an amount filter: parse the German input through the shared
   * `parseAmountInput` and patch the canonical dot-decimal string the backend
   * expects. Invalid input never reaches the URL/query — the field is flagged
   * and an inline hint is shown instead of firing a doomed 422 request.
   */
  const commitAmount = (field: 'amount_min' | 'amount_max', flag: 'min' | 'max') => {
    return (value: string) => {
      if (value === '') {
        setAmountInvalid((prev) => ({ ...prev, [flag]: false }));
        if (filters[field]) patch({ [field]: undefined });
        return;
      }
      const parsed = parseAmountInput(value);
      if (parsed === null) {
        setAmountInvalid((prev) => ({ ...prev, [flag]: true }));
        return;
      }
      setAmountInvalid((prev) => ({ ...prev, [flag]: false }));
      if ((filters[field] ?? '') !== parsed) patch({ [field]: parsed });
    };
  };

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 sm:px-6 dark:border-slate-800/70">
      <div className="group relative min-w-0 flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors duration-150 group-focus-within:text-sky-600 dark:group-focus-within:text-sky-400" />
        <CommitInput
          key={`q:${filters.q ?? ''}`}
          initialValue={filters.q ?? ''}
          onCommit={(value) => {
            if ((filters.q ?? '') !== value) patch({ q: value || undefined });
          }}
          type="search"
          placeholder="Empfänger, Verwendungszweck …"
          className="pl-10"
          aria-label="Transaktionen durchsuchen"
        />
      </div>

      <div className="w-52 shrink-0">
        <Select
          value={filters.sort ?? DEFAULT_SORT}
          onValueChange={(value) =>
            patch({ sort: value === DEFAULT_SORT ? undefined : (value as TransactionSort) })
          }
          options={SORT_OPTIONS}
        />
      </div>

      <Popover
        open={open}
        onOpenChange={setOpen}
        align="end"
        className="w-80"
        trigger={
          <Button variant="secondary" size="sm">
            <Filter className="h-4 w-4" />
            Filter
            {activeCount > 0 ? (
              <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-600 px-1.5 text-xs font-semibold tabular-nums text-white dark:bg-sky-500">
                {activeCount}
              </span>
            ) : null}
          </Button>
        }
      >
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Filter</p>
            {activeCount > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setAmountInvalid({ min: false, max: false });
                  reset();
                }}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 transition-colors duration-150 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
              >
                <X className="h-3.5 w-3.5" />
                Zurücksetzen
              </button>
            ) : null}
          </div>

          <FilterSection title="Status">
            {REVIEW_ORDER.map((value) => (
              <CheckRow
                key={value}
                label={REVIEW_STATUS[value]}
                checked={filters.review_status?.includes(value) ?? false}
                onToggle={() => patch({ review_status: toggle(filters.review_status, value) })}
              />
            ))}
          </FilterSection>

          <FilterSection title="Buchungsstatus">
            {STATUS_ORDER.map((value) => (
              <CheckRow
                key={value}
                label={STATUS[value]}
                checked={filters.status?.includes(value) ?? false}
                onToggle={() => patch({ status: toggle(filters.status, value) })}
              />
            ))}
          </FilterSection>

          <FilterSection title="Konten">
            {accounts.length === 0 ? (
              <p className="px-1 text-sm text-slate-400">Keine Konten</p>
            ) : (
              <div className="max-h-40 space-y-0.5 overflow-y-auto">
                {accounts.map((account) => (
                  <CheckRow
                    key={account.id}
                    label={account.name}
                    checked={filters.account_id?.includes(account.id) ?? false}
                    onToggle={() => patch({ account_id: toggle(filters.account_id, account.id) })}
                  />
                ))}
              </div>
            )}
          </FilterSection>

          <FilterSection title="Kategorien">
            {buckets.length === 0 ? (
              <p className="px-1 text-sm text-slate-400">Keine Kategorien</p>
            ) : (
              <div className="max-h-40 space-y-0.5 overflow-y-auto">
                {buckets.map((bucket) => (
                  <CheckRow
                    key={bucket.id}
                    label={bucket.icon ? `${bucket.icon} ${bucket.name}` : bucket.name}
                    checked={filters.bucket_id?.includes(bucket.id) ?? false}
                    onToggle={() => patch({ bucket_id: toggle(filters.bucket_id, bucket.id) })}
                  />
                ))}
              </div>
            )}
          </FilterSection>

          <FilterSection title="Zeitraum">
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                aria-label="Von"
                value={filters.date_from ?? ''}
                onChange={(event) => patch({ date_from: event.target.value || undefined })}
              />
              <Input
                type="date"
                aria-label="Bis"
                value={filters.date_to ?? ''}
                onChange={(event) => patch({ date_to: event.target.value || undefined })}
              />
            </div>
          </FilterSection>

          <FilterSection title="Betrag (€)">
            <div className="grid grid-cols-2 gap-2">
              <CommitInput
                key={`min:${filters.amount_min ?? ''}`}
                initialValue={filters.amount_min ? formatAmountInput(filters.amount_min) : ''}
                onCommit={commitAmount('amount_min', 'min')}
                invalid={amountInvalid.min}
                type="text"
                inputMode="decimal"
                aria-label="Betrag von"
                placeholder="min."
              />
              <CommitInput
                key={`max:${filters.amount_max ?? ''}`}
                initialValue={filters.amount_max ? formatAmountInput(filters.amount_max) : ''}
                onCommit={commitAmount('amount_max', 'max')}
                invalid={amountInvalid.max}
                type="text"
                inputMode="decimal"
                aria-label="Betrag bis"
                placeholder="max."
              />
            </div>
            {amountInvalid.min || amountInvalid.max ? (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                Ungültiger Betrag – bitte eine Zahl wie „-49,90“ eingeben.
              </p>
            ) : null}
          </FilterSection>

          <FilterSection title="Tags">
            <TagInput
              value={filters.tag ?? []}
              onChange={(tags) => patch({ tag: tags.length ? tags : undefined })}
            />
          </FilterSection>

          <CheckRow
            label="Gelöschte anzeigen"
            checked={filters.include_deleted ?? false}
            onToggle={() => patch({ include_deleted: filters.include_deleted ? undefined : true })}
          />
        </div>
      </Popover>
    </div>
  );
}
